import React, { useState, useEffect } from 'react';
import { post, get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';

const CrearClienteForm = ({ marcaId, tiendas, onClose, onClienteCreated }) => {
    // Estados para marcas y tiendas disponibles (en caso de no recibirse por props)
    const [marcas, setMarcas] = useState([]);
    const [tiendasDisponibles, setTiendasDisponibles] = useState(tiendas || []);

    // Estados para teléfono: prefijo y número
    const [phonePrefix, setPhonePrefix] = useState('+34'); // Por defecto: España
    const [customPrefix, setCustomPrefix] = useState('');

    // Estado para el código de referido ingresado por el usuario
    const [codigoReferido, setCodigoReferido] = useState('');

    // Estado inicial del cliente
    const [cliente, setCliente] = useState({
        nombre: '',
        apellido: '',
        email: '',
        // El campo "telefono" se completará al enviar con el prefijo concatenado al número
        telefono: '',
        fechaCumpleanos: '',
        marca: marcaId || '',
        // Si se pasó tiendas y sólo hay una, se asigna automáticamente
        tienda: (tiendas && tiendas.length === 1) ? tiendas[0]._id : '',
        genero: 'PREFIERO_NO_DECIRLO', // Valor por defecto
        geographic_info: {
            country: '',
            state: '',
            city: '',
            postal_code: ''
        },
        // Este campo guardará el ID del cliente que refirió al nuevo cliente (si aplica)
        referidoPor: ''
    });

    // Cargar marcas y tiendas si no se recibieron por props
    useEffect(() => {
        const loadData = async () => {
            try {
                if (!marcaId) {
                    const fetchedMarcas = await get('marcas');
                    setMarcas(fetchedMarcas);
                    if (fetchedMarcas.length === 1) {
                        setCliente(prev => ({ ...prev, marca: fetchedMarcas[0]._id }));
                        const fetchedTiendas = await get(`tiendas?marcaId=${fetchedMarcas[0]._id}`);
                        setTiendasDisponibles(fetchedTiendas);
                        if (fetchedTiendas.length === 1) {
                            setCliente(prev => ({ ...prev, tienda: fetchedTiendas[0]._id }));
                        }
                    }
                } else {
                    if (tiendasDisponibles.length === 0) {
                        const fetchedTiendas = await get(`tiendas?marcaId=${marcaId}`);
                        setTiendasDisponibles(fetchedTiendas);
                        if (fetchedTiendas.length === 1) {
                            setCliente(prev => ({ ...prev, tienda: fetchedTiendas[0]._id }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error al cargar marcas o tiendas:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar las marcas o tiendas. Intenta nuevamente.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        };
        loadData();
    }, [marcaId, tiendasDisponibles.length]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('geographic_info')) {
            const [group, key] = name.split('.');
            setCliente(prev => ({
                ...prev,
                [group]: { ...prev[group], [key]: value }
            }));
        } else {
            setCliente(prev => ({ ...prev, [name]: value }));
        }
    };

    // Maneja el cambio en el input de código de referido
    const handleCodigoReferidoChange = (e) => {
        setCodigoReferido(e.target.value);
    };

    const handlePrefixChange = (e) => {
        setPhonePrefix(e.target.value);
        if (e.target.value !== 'otros') {
            setCustomPrefix('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validar que se proporcione email y teléfono (teléfono obligatorio)
        if (!cliente.email || !cliente.telefono) {
            Swal.fire({
                title: 'Error',
                text: 'Debe proporcionar al menos un email y un teléfono',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }
        // Determinar el prefijo final y concatenarlo con el número
        const finalPrefix = phonePrefix === 'otros' ? customPrefix : phonePrefix;
        const telefonoCompleto = `${finalPrefix}${cliente.telefono}`;
        const clienteToSend = { ...cliente, telefono: telefonoCompleto };

        // Si se ingresó un código de referido, validar que exista y pertenezca a un cliente de la misma marca
        if (codigoReferido.trim() !== '') {
            // Limpiar el código: quitar espacios y convertir a minúsculas
            const cleanedCode = codigoReferido.trim().toLowerCase().replace(/\s+/g, '');
            try {
                // Suponemos que existe un endpoint que valida el código de referido:
                // GET /clientes/referral?code=cleanedCode&marcaId=...
                const refResponse = await get(`clientes/referral?code=${cleanedCode}&marcaId=${clienteToSend.marca}`);
                if (!refResponse || !refResponse._id) {
                    Swal.fire({
                        title: 'Error',
                        text: 'El código de referido no es válido o no pertenece a un cliente de tu marca',
                        icon: 'error',
                        confirmButtonText: 'Aceptar'
                    });
                    return;
                }
                // Si es válido, asignar el id del cliente referido al nuevo cliente
                clienteToSend.referidoPor = refResponse._id;
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'Error al validar el código de referido',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }
        }

        try {
            await post('clientes', clienteToSend);
            Swal.fire({
                title: 'Éxito',
                text: 'Cliente creado correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });
            onClienteCreated();
            onClose();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md sm:max-w-lg lg:max-w-2xl mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">Crear Cliente</h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Formulario */}
                        <form onSubmit={handleSubmit} className="mt-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Campos obligatorios */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre*</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        value={cliente.nombre}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="Ingresa el nombre"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Apellido*</label>
                                    <input
                                        type="text"
                                        name="apellido"
                                        value={cliente.apellido}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="Ingresa el apellido"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={cliente.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="ejemplo@email.com"
                                    />
                                </div>

                                {/* Teléfono: prefijo + número */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono*</label>
                                    <div className="flex space-x-2">
                                        <select
                                            name="phonePrefix"
                                            value={phonePrefix}
                                            onChange={handlePrefixChange}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm min-w-[120px]"
                                        >
                                            <option value="+34">España (+34)</option>
                                            <option value="+51">Perú (+51)</option>
                                            <option value="+56">Chile (+56)</option>
                                            <option value="otros">Otros</option>
                                        </select>
                                        {phonePrefix === 'otros' && (
                                            <input
                                                type="text"
                                                name="customPrefix"
                                                value={customPrefix}
                                                onChange={(e) => setCustomPrefix(e.target.value)}
                                                placeholder="Prefijo"
                                                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm w-20"
                                            />
                                        )}
                                        <input
                                            type="text"
                                            name="telefono"
                                            value={cliente.telefono}
                                            onChange={handleChange}
                                            required
                                            placeholder="Número de teléfono"
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Fecha de Cumpleaños *
                                    </label>
                                    <input
                                        type="date"
                                        name="fechaCumpleanos"
                                        placeholder="Fecha de Cumpleaños *"
                                        value={cliente.fechaCumpleanos}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Género
                                    </label>
                                    <select
                                        name="genero"
                                        value={cliente.genero}
                                        onChange={handleChange}
                                        className="w-full border rounded-lg px-3 py-2"
                                    >
                                        <option value="HOMBRE">Hombre</option>
                                        <option value="MUJER">Mujer</option>
                                        <option value="NO_BINARIO">No binario</option>
                                        <option value="PREFIERO_NO_DECIRLO">Prefiero no decirlo</option>
                                    </select>
                                </div>

                                {/* Código de referido (opcional) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Código de referido</label>
                                    <input
                                        type="text"
                                        name="codigoReferido"
                                        value={codigoReferido}
                                        onChange={handleCodigoReferidoChange}
                                        placeholder="Código de referido (opcional)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    />
                                </div>

                                {/* Select de Marca: se muestra si no se recibió marcaId por props */}
                                {!marcaId && marcas.length > 0 && (
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                                        <select
                                            name="marca"
                                            value={cliente.marca}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        >
                                            {marcas.map(m => (
                                                <option key={m._id} value={m._id}>
                                                    {m.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Select de Tienda */}
                                {(!cliente.tienda || (tiendasDisponibles && tiendasDisponibles.length > 1)) && tiendasDisponibles.length > 0 && (
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Tienda</label>
                                        <select
                                            name="tienda"
                                            value={cliente.tienda}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        >
                                            {tiendasDisponibles.map(t => (
                                                <option key={t._id} value={t._id}>
                                                    {t.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Datos geográficos */}
                                <div className="md:col-span-2">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3 border-t pt-4">Información Geográfica (Opcional)</h4>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                                    <input
                                        type="text"
                                        name="geographic_info.country"
                                        value={cliente.geographic_info.country}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="País"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado/Región</label>
                                    <input
                                        type="text"
                                        name="geographic_info.state"
                                        value={cliente.geographic_info.state}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="Estado o región"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
                                    <input
                                        type="text"
                                        name="geographic_info.city"
                                        value={cliente.geographic_info.city}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="Ciudad"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Código Postal</label>
                                    <input
                                        type="text"
                                        name="geographic_info.postal_code"
                                        value={cliente.geographic_info.postal_code}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                        placeholder="Código postal"
                                    />
                                </div>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                                >
                                    Crear Cliente
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CrearClienteForm;
